import { auth, db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { PublicProfileService } from './PublicProfileService';

const COUNT_KEY  = 'streak_count';
const LAST_KEY   = 'streak_last_day';
const FREEZE_KEY = 'streak_freezes';
const MAX_FREEZES = 3;

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function dateNDaysAgoStr(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getFreezesRaw() {
  return parseInt(localStorage.getItem(FREEZE_KEY) || '0', 10);
}

function expireIfBroken() {
  const last = localStorage.getItem(LAST_KEY);
  if (!last) return;
  const today = todayStr();
  const yesterday = dateNDaysAgoStr(1);
  if (last === today || last === yesterday) return;

  // Exactly one missed day: spend a streak freeze (if available) to forgive
  // it rather than resetting to 0 — shifting LAST_KEY to "yesterday" lets
  // today's recordActivity() continue the streak normally. A gap of 2+
  // missed days is too large for a single freeze to cover.
  const freezes = getFreezesRaw();
  if (last === dateNDaysAgoStr(2) && freezes > 0) {
    localStorage.setItem(FREEZE_KEY, String(freezes - 1));
    localStorage.setItem(LAST_KEY, yesterday);
    return;
  }
  localStorage.setItem(COUNT_KEY, '0');
}

export const StreakService = {
  getStreak() {
    expireIfBroken();
    return parseInt(localStorage.getItem(COUNT_KEY) || '0', 10);
  },

  getFreezes() {
    return getFreezesRaw();
  },

  async recordActivity() {
    expireIfBroken();
    const today = todayStr();
    const last  = localStorage.getItem(LAST_KEY);
    if (last === today) return this.getStreak();

    const yesterday = dateNDaysAgoStr(1);
    const prev = parseInt(localStorage.getItem(COUNT_KEY) || '0', 10);
    const streak = last === yesterday ? prev + 1 : 1;

    localStorage.setItem(COUNT_KEY, String(streak));
    localStorage.setItem(LAST_KEY, today);

    // Earn a streak freeze every 7-day milestone, capped at MAX_FREEZES.
    if (streak % 7 === 0) {
      const freezes = getFreezesRaw();
      if (freezes < MAX_FREEZES) {
        localStorage.setItem(FREEZE_KEY, String(freezes + 1));
      }
    }

    const uid = auth?.currentUser?.uid;
    if (uid && db) {
      setDoc(doc(db, 'users', uid), {
        streak, streakLastDay: today, streakFreezes: getFreezesRaw(),
      }, { merge: true }).catch((e) => console.warn('StreakService: Firestore sync failed', e));
      PublicProfileService.sync({ streak }).catch(() => {});
    }
    return streak;
  },

  /**
   * Reconciles the local streak with Firestore on login so a fresh device or
   * cleared localStorage doesn't start at 0 and silently overwrite a real
   * cloud streak on the next recordActivity() write. Adopts the remote
   * streak only when it's at least as current as what's stored locally
   * (remote's last-active day is on or after local's, or there is no local
   * streak yet) — otherwise the local streak is already ahead and stays.
   */
  hydrateFromRemote(remoteData) {
    const remoteStreak = Number(remoteData?.streak) || 0;
    const remoteLastDay = remoteData?.streakLastDay || '';
    if (remoteStreak && remoteLastDay) {
      const localLastDay = localStorage.getItem(LAST_KEY) || '';
      if (!localLastDay || remoteLastDay >= localLastDay) {
        localStorage.setItem(COUNT_KEY, String(remoteStreak));
        localStorage.setItem(LAST_KEY, remoteLastDay);
      }
    }
    const remoteFreezes = Number(remoteData?.streakFreezes) || 0;
    if (remoteFreezes > getFreezesRaw()) {
      localStorage.setItem(FREEZE_KEY, String(remoteFreezes));
    }
  },
};
